import * as Yup from "yup";
import { ERRORS } from "../constants/base";
import { isValidPhone } from "../utils/phoneNumber";

/**
 * The `phones` array rule, shared by every form that edits contact phone numbers.
 *
 * A number is not REQUIRED here — most phone slots are optional and each form decides that
 * for itself. What this enforces is that a number the user DID type looks like one: 7-15
 * digits with an optional leading "+", the same loose worldwide rule the API applies
 * (App\Rules\PhoneNumber). Without it the form submits happily and the server answers 422,
 * which surfaces as a toast rather than an error on the offending field.
 *
 * @see ../utils/phoneNumber
 */
export const phoneNumberFieldSchema = () =>
  Yup.string()
    .nullable()
    .test("phone-number-shape", ERRORS.phone, (number) => isValidPhone(number));

export const phonesArraySchema = () =>
  Yup.array()
    .nullable()
    .of(Yup.object().shape({ number: phoneNumberFieldSchema() }));

/**
 * Does a Yup error tree contain a phone-shape failure anywhere?
 *
 * A blocked submit otherwise shows the generic "fill in the required fields" toast, which is
 * actively misleading when the field IS filled in and merely unusable. Formik nests errors to
 * an arbitrary depth (`persons[3].phones[1].number`, `housePhone.number`), so this walks the
 * whole tree rather than probing known paths.
 */
export const hasPhoneError = (errors: unknown): boolean => {
  const seen = new Set<unknown>();

  const walk = (node: unknown): boolean => {
    if (typeof node === "string") return node === ERRORS.phone;
    if (node === null || typeof node !== "object") return false;
    // Formik does not produce cyclic errors, but a caller might hand us anything.
    if (seen.has(node)) return false;
    seen.add(node);
    return Object.values(node as Record<string, unknown>).some(walk);
  };

  return walk(errors);
};
